
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { toDatabaseValue, isDatabaseRecord } from "@/utils/supabase/type-guards";
import { safeSelect } from "@/utils/supabase/queries";

export type Notification = {
  id: string;
  content: string;
  type: string;
  relatedId?: string;
  isRead: boolean;
  createdAt: string;
};

export function useNotifications() {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      setNotifications([]);
      setUnreadCount(0);
      setLoading(false);
      return;
    }
    
    const fetchNotifications = async () => {
      setLoading(true);
      
      try {
        const data = await safeSelect<any>(
          supabase
            .from('notifications')
            .select('*')
            .eq('user_id', toDatabaseValue(user.id))
            .order('created_at', { ascending: false })
            .limit(50),
          "Failed to load notifications"
        );
        
        if (data && Array.isArray(data)) {
          // Transform from database schema to our app's Notification type
          const formattedNotifications: Notification[] = data
            .filter(item => isDatabaseRecord(item))
            .map(item => ({
              id: item.id,
              content: item.content,
              type: item.type,
              relatedId: item.related_id,
              isRead: item.is_read,
              createdAt: item.created_at
            }));
          
          setNotifications(formattedNotifications);
          setUnreadCount(formattedNotifications.filter(n => !n.isRead).length);
        } else {
          setNotifications([]);
          setUnreadCount(0);
        }
      } catch (error: any) {
        console.error("Error fetching notifications:", error);
        toast.error("Failed to load notifications");
        setNotifications([]);
        setUnreadCount(0);
      } finally {
        setLoading(false);
      }
    };
    
    // Initial fetch
    fetchNotifications();
    
    // Set up realtime subscription
    const channel = supabase
      .channel('public:notifications')
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'notifications',
        filter: `user_id=eq.${user.id}`
      }, (payload) => {
        // Add new notification to the list
        const newNotification: Notification = {
          id: payload.new.id,
          content: payload.new.content,
          type: payload.new.type,
          relatedId: payload.new.related_id,
          isRead: payload.new.is_read,
          createdAt: payload.new.created_at
        };
        
        setNotifications(prev => [newNotification, ...prev]);
        setUnreadCount(prev => prev + 1);
        toast.info(payload.new.content, {
          description: "You have a new notification",
        });
      })
      .subscribe();
    
    // Cleanup
    return () => {
      supabase.removeChannel(channel);
    };
  }, [user]);
  
  const markAsRead = async (notificationId: string) => {
    if (!user) return;
    
    try {
      const { error } = await supabase
        .from('notifications')
        .update(toDatabaseValue({ is_read: true }))
        .eq('id', toDatabaseValue(notificationId))
        .eq('user_id', toDatabaseValue(user.id));
        
      if (error) throw error;
      
      setNotifications(prev => 
        prev.map(n => 
          n.id === notificationId ? { ...n, isRead: true } : n
        )
      );
      
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error: any) {
      console.error("Error marking notification as read:", error);
      toast.error("Failed to update notification");
    }
  };
  
  const markAllAsRead = async () => {
    if (!user) return;
    
    try {
      const { error } = await supabase
        .from('notifications')
        .update(toDatabaseValue({ is_read: true }))
        .eq('user_id', toDatabaseValue(user.id))
        .eq('is_read', toDatabaseValue(false));
        
      if (error) throw error;
      
      setNotifications(prev => 
        prev.map(n => ({ ...n, isRead: true }))
      );
      
      setUnreadCount(0);
      toast.success("All notifications marked as read");
    } catch (error: any) {
      console.error("Error marking all notifications as read:", error);
      toast.error("Failed to update notifications");
    }
  };

  return {
    notifications,
    unreadCount,
    loading,
    markAsRead,
    markAllAsRead
  };
}
